<template>
  <main class="page-phones">
    <div class="container-wide">
      <PhonesHeadline />
      <mq-layout mq="lg">
        <PhonesFilters />
      </mq-layout>
      <PhonesGraphs v-show="showGraphs" />
      <PhonesGrid @toggle-phone-edit="togglePhoneEdit" />
    </div>
    <transition name="fade">
      <PhonesGridContentEdit v-if="phoneEditVisible" @toggle-phone-edit2="togglePhoneEdit" />
    </transition>
  </main>
</template>

<script lang="ts">
import axios, { CancelTokenSource } from "axios";
import moment from "moment";
import VueMq from "vue-mq";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import PhonesFilters from "@/components/Phones/PhonesFilters.vue";
import PhonesGraphs from "@/components/Phones/PhonesGraphs.vue";
import PhonesGrid from "@/components/Phones/PhonesGrid.vue";
import PhonesGridContentEdit from "@/components/Phones/PhonesGridContentEdit.vue";
import PhonesHeadline from "@/components/Phones/PhonesHeadline.vue";
import GridTableSettingsEn from "@/data/grid-defaultsettings-en.json";
import GridTableSettingsHe from "@/data/grid-defaultsettings-he.json";
import { Meta } from "@/decorators/MetaDecorator";
import { RefreshGridMode } from "@/models/leads/ContactForm";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { PhoneLeadStats } from "@/models/leads/LeadStatistic";
import { MetadataMaskyooStatus } from "@/models/MetadataMaskyooStatus";
import { MultiselectItem } from "@/models/MultiselectItem";
import { PhonesPageData } from "@/models/phones/PhonesPageData";
import { PhoneStatus } from "@/models/phones/PhoneWithChannel";
import { TableSettings, TableSettingsResponse, TableSettingsState } from "@/models/TableSettings";
import HelperService from "@/services/HelperService";
import PhoneService from "@/services/PhoneService";
import ApiService, { LeadsPayload, LeadsStatisticPhonesPayload } from "@/services/api/ApiService";
import { PhonesPayload } from "@/services/api/PhonesApiService";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});

const namespace = "phonesModule";
const leadsNamespace = "leadsModule";

@Component({
  components: {
    PhonesHeadline,
    PhonesFilters,
    PhonesGraphs,
    PhonesGrid,
    PhonesGridContentEdit,
  },
})
export default class Phones extends Vue {

  @Meta
  myMetaConfig() {
    return {
      title: i18n.t("Phones.Title").toString()
    };
  }

  //#region Vuex getters and actions

  @Getter("cancelTokenSource", { namespace }) cancelTokenSource!: CancelTokenSource | undefined;
  @Getter("columnsFilters", { namespace }) columnsFilters!: FilterCollection;
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("needToRefreshPhonesGrid", { namespace }) needToRefreshPhonesGrid!: RefreshGridMode;
  @Getter("page", { namespace }) page!: number;
  @Getter("phoneLeadStats", { namespace }) phoneLeadStats!: PhoneLeadStats | undefined;
  @Getter("phonesPageData", { namespace }) phonesPageData!: PhonesPageData;
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("selectedCallStatus", { namespace }) selectedCallStatus!: MetadataMaskyooStatus;
  @Getter("selectedChannelMetadataTypesIds", { namespace }) selectedChannelMetadataTypesIds!: number[];
  @Getter("selectedClientsIds", { namespace }) selectedClientsIds!: number[];
  @Getter("selectedPhoneStatus", { namespace }) selectedPhoneStatus!: PhoneStatus;
  @Getter("selectedSourcesIds", { namespace }) selectedSourcesIds!: string[];
  @Getter("showGraphs", { namespace }) showGraphs!: boolean;
  @Getter("sortDir", { namespace }) sortDir!: string;
  @Getter("sortField", { namespace }) sortField!: string;
  @Getter("sources", { namespace }) sources!: MultiselectItem[];
  @Action("getClients", { namespace }) getClients!: () => Promise<MultiselectItem[]>;
  @Action("getLeadsStatisticPhones", { namespace: leadsNamespace }) getLeadsStatisticPhones!: (payload: LeadsPayload) => Promise<PhoneLeadStats>;
  @Action("getPhones", { namespace }) getPhones!: (payload: PhonesPayload) => Promise<DataSourceResult>;
  @Action("getPhonesPageData", { namespace }) getPhonesPageData!: () => Promise<PhonesPageData>;
  @Action("setCancelTokenSource", { namespace }) setCancelTokenSource!: (cancelTokenSource: CancelTokenSource) => void;
  @Action("setLeadsStatisticPhones", { namespace: leadsNamespace }) setLeadsStatisticPhones!: (phoneLeadStats?: PhoneLeadStats) => void;
  @Action("setNeedToRefreshPhonesGrid", { namespace }) setNeedToRefreshPhonesGrid!: (needToRefreshPhonesGrid: RefreshGridMode) => void;
  @Action("setPhones", { namespace }) setPhones!: (phones: DataSourceResult) => void;
  @Action("setSelectedClientsIds", { namespace }) setSelectedClientsIds!: (selectedClientsIds: number[]) => void;
  @Action("setTableSettings", { namespace }) setTableSettings!: (tableSettings: TableSettings | undefined) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  PAGE_FILTERS_COOKIE_NAME = "page_filters";
  pageSize = 30;
  token = "";
  
  public phoneEditVisible = false;

  //#endregion Properties


  //#region Watchers

  @Watch("datePeriod", { immediate: true })
  onDatePeriodChange(newDatePeriod: Date[], oldDatePeriod: Date[]): void {
    if (oldDatePeriod === undefined || newDatePeriod === undefined || oldDatePeriod.length === 0
          || moment(newDatePeriod[0]).format("YYYY-MM-DD") === moment(oldDatePeriod[0]).format("YYYY-MM-DD")
            && moment(newDatePeriod[1]).format("YYYY-MM-DD") === moment(oldDatePeriod[1]).format("YYYY-MM-DD")) {
      return;
    } else {
      this.refreshGrid(RefreshGridMode.GraphsOnly);
    }
  }

  @Watch("needToRefreshPhonesGrid", { immediate: true })
  onNeedToRefreshLeadsGrid(newValue: RefreshGridMode, oldValue: RefreshGridMode): void {
    if (oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(newValue);
    }
  }

  @Watch("searchValue", { immediate: true })
  onSearchValueChange(newValue: string, oldValue: string): void {
    if (oldValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  //#endregion Watchers


  //#region Lifecycle

  mounted(): void {
    this.token = HelperService.guid();
    const cookieValue: string = Vue.$cookies.get(this.PAGE_FILTERS_COOKIE_NAME);
    if (!cookieValue) {
      PhoneService.resetFilters(false);
    }
    this.getPhonesPageData().then((data: PhonesPageData) => {
      if (data.ClientId) {
        this.setSelectedClientsIds([data.ClientId]);
      } else {
        this.getClients().then(() => {
          if (this.$route.query.clientId) {
            this.setSelectedClientsIds([+this.$route.query.clientId]);
          }
        });
        if (!this.$route.query.clientId) {
          this.refreshGrid();
        }
      }
    });
    this.setTableSettings(undefined);
    ApiService.getTableSettings(i18n.locale, this.$route.path).then((data: TableSettingsResponse) => {
      if (data.settings.length > 0) {
        data.settings.forEach((settings: TableSettings) => {
          settings.StateObject = JSON.parse(settings.State);
          this.setTableSettings(settings);
        });
      } else {
        let settingsState: TableSettingsState = GridTableSettingsHe;
        switch (i18n.locale) {
          case "en-US":
            settingsState = GridTableSettingsEn;
            break;
          case "he-IL":
            settingsState = GridTableSettingsHe;
            break;
        }
        this.setTableSettings({
          Culture: i18n.locale,
          GridId: "phonesgrid",
          State: JSON.stringify(settingsState),
          StateObject: settingsState,
          Url: location.pathname
        });
      }
    });
  }

  //#endregion Lifecycle


  //#region Handlers

  public popupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  public popupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  public togglePhoneEdit(): void {
    this.phoneEditVisible = !this.phoneEditVisible;
    this.phoneEditVisible ? this.popupBodyOn() : this.popupBodyOff();
  }

  //#endregion Handlers


  //#region Private methods

  private refreshGrid(mode: RefreshGridMode = RefreshGridMode.Full): void {
    if (this.cancelTokenSource) {
      this.cancelTokenSource?.cancel();
    }
    this.setNeedToRefreshPhonesGrid(RefreshGridMode.None);
    this.token = HelperService.guid();
    if (mode !== RefreshGridMode.GridOnly) {
      this.setLeadsStatisticPhones(undefined);
    }
    const filterCollection: FilterCollection = {
      logic: "and",
      filters: [],
    };
    const leadsStatFilterCollection: FilterCollection = {
      logic: "and",
      filters: [],
    };
    if (this.selectedChannelMetadataTypesIds.length) {
      filterCollection.filters?.push(HelperService.setFilterArray(["ChannelMetadataTypeID"], this.selectedChannelMetadataTypesIds, "eq"));
    }
    if (this.selectedPhoneStatus) {
      filterCollection.filters?.push({
        field: "StatusID",
        operator: "eq",
        value: this.selectedPhoneStatus.toString(),
      });
    }
    if (this.searchValue) {
      filterCollection.filters?.push(HelperService.setFilterArray(["PhoneNumber", "RedirectPhone"], [this.searchValue], "contains"));
    }
    if (this.columnsFilters && this.columnsFilters.filters && this.columnsFilters.filters?.length > 0) {
      filterCollection.filters?.push(this.columnsFilters);
    }
    if (this.phonesPageData?.ClientId) {
      filterCollection.filters?.push(HelperService.setFilterArray(["CRMClientID"], [this.phonesPageData.ClientId], "eq"));
    } else if (this.selectedClientsIds.length) {
      filterCollection.filters?.push(HelperService.setFilterArray(["CRMClientID"], this.selectedClientsIds, "eq"));
      leadsStatFilterCollection.filters?.push(HelperService.setFilterArray(["ClientID"], this.selectedClientsIds, "eq"));
    }

    const filtersString: string = HelperService.getFiltersString(filterCollection);
    const selectedSourcesNames: string[] = this.sources.filter((i: MultiselectItem) => this.selectedSourcesIds.indexOf(i.ID) >= 0)
          .map((i: MultiselectItem) => i.Title);
    const phonesPayload: PhonesPayload = {
      channelNames: selectedSourcesNames,
      filter: filtersString,
      page: this.page,
      pageSize: this.pageSize,
      sort: `${ this.sortField }-${ this.sortDir }`,
    };

    if (this.selectedCallStatus !== MetadataMaskyooStatus.All) {
      leadsStatFilterCollection.filters?.push({
        field: "StatusCall",
        operator: "eq",
        value: this.selectedCallStatus.toString(),
      });
    }

    const selectedChannelsIds: string[] = this.selectedSourcesIds.filter((i: string) => i.indexOf(":") === -1);
    const selectedAssetsIds: string[] = this.selectedSourcesIds.filter((i: string) => i.indexOf(":") >= 0);
    if (selectedChannelsIds.length || selectedAssetsIds.length) {
      const channelsAndAssetsFilterCollection: FilterCollection = {
        logic: "or",
        filters: [],
      };
      if (selectedChannelsIds.length) {
        channelsAndAssetsFilterCollection.filters?.push(HelperService.setFilterArray(["ChannelID"], selectedChannelsIds, "eq"));
      }
      if (selectedAssetsIds.length) {
        channelsAndAssetsFilterCollection.filters?.push(HelperService.setFilterArray(["AssetId"], selectedAssetsIds, "eq"));
      }
      leadsStatFilterCollection.filters?.push(channelsAndAssetsFilterCollection);
    }

    const leadsStatisticPhonesPayload: LeadsStatisticPhonesPayload = {
      dateFrom: this.datePeriod[0] ? moment(this.datePeriod[0]).format("YYYY-MM-DD") : undefined,
      dateTo: this.datePeriod[0] ? moment(this.datePeriod[1]).format("YYYY-MM-DD") : undefined,
      filter: HelperService.getFiltersString(leadsStatFilterCollection),
      pageSize: this.pageSize,
      phoneStatus: this.selectedPhoneStatus,
      channelMetadataTypes: this.selectedChannelMetadataTypesIds,
      token: this.token,
    };

    if (mode === RefreshGridMode.Full) {
      this.getPhones(phonesPayload).then((phones: DataSourceResult) => {
        this.getLeadsStatisticPhones(leadsStatisticPhonesPayload);
      }).finally(() => {
        this.setNeedToRefreshPhonesGrid(RefreshGridMode.None);
      });
    } else if (mode === RefreshGridMode.GraphsOnly) {
      this.getLeadsStatisticPhones(leadsStatisticPhonesPayload).finally(() => {
        this.setNeedToRefreshPhonesGrid(RefreshGridMode.None);
      });
    } else if (mode === RefreshGridMode.GridOnly) {
      this.getPhones(phonesPayload).then((phonesResult: DataSourceResult) => {
        // after sorting still need to set statistics
        if (phonesResult?.Data && this.phoneLeadStats) {
          const newPhones: DataSourceResult = PhoneService.setStatsForPhones(phonesResult, this.phoneLeadStats);
          this.setPhones(newPhones);
        }
      }).finally(() => {
        this.setNeedToRefreshPhonesGrid(RefreshGridMode.None);
      });
    }

    Vue.$cookies.set(this.PAGE_FILTERS_COOKIE_NAME, true, "8h");
  }

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
.page-phones {
}
</style>
